import {
  CalendarPlus,
  Check,
  ChevronDown,
  ListChecks,
  MessageSquareMore,
  Pencil,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import api from "../api/client";
import { ErrorNotice, LoadingState, SuccessNotice } from "../components/Feedback";
import PageHeader from "../components/PageHeader";
import StatusBadge from "../components/StatusBadge";
import { useAuth } from "../context/AuthContext";
import { useWorkspace } from "../context/WorkspaceContext";
import type { LeaveEntry, LeaveStatus, Paginated } from "../types/api";
import { unwrapResults } from "../types/api";
import { apiErrorMessage } from "../utils/apiError";
import { formatDate, formatMinutes } from "../utils/format";

const filters: Array<{ value: "all" | LeaveStatus; label: string }> = [
  { value: "all", label: "Alles" },
  { value: "draft", label: "Concept" },
  { value: "requested", label: "In afwachting" },
  { value: "approved", label: "Goedgekeurd" },
  { value: "rejected", label: "Afgewezen" },
];

type BulkAction = "request" | "approve" | "delete";
type BulkResult = {
  action: BulkAction;
  succeeded: number[];
  failed: Array<{ id: number; detail: string }>;
};

export default function EntriesPage() {
  const { user } = useAuth();
  const { organizationId, organization, capabilities } = useWorkspace();
  const [entries, setEntries] = useState<LeaveEntry[]>([]);
  const [filter, setFilter] = useState<"all" | LeaveStatus>("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [workingId, setWorkingId] = useState<number | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [bulkWorking, setBulkWorking] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const menuRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const context = organizationId
        ? `organization=${organizationId}`
        : "context=personal";
      const response = await api.get<Paginated<LeaveEntry> | LeaveEntry[]>(
        `/leave/entries/?ordering=-start_date&${context}`,
      );
      setEntries(unwrapResults(response.data));
    } catch {
      setError("Je verlofregels konden niet worden geladen.");
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Een selectie hoort bij de regels die nu op het scherm staan; na een
  // filterwissel of herladen mag er niets onzichtbaars geselecteerd blijven.
  useEffect(() => {
    setSelected([]);
    setMenuOpen(false);
  }, [organizationId]);

  useEffect(() => {
    if (!menuOpen) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    window.document.addEventListener("mousedown", closeOnOutsideClick);
    window.document.addEventListener("keydown", closeOnEscape);
    return () => {
      window.document.removeEventListener("mousedown", closeOnOutsideClick);
      window.document.removeEventListener("keydown", closeOnEscape);
    };
  }, [menuOpen]);

  const visibleEntries = useMemo(
    () =>
      entries.filter((entry) => {
        if (filter !== "all" && entry.status !== filter) return false;
        const query = search.trim().toLowerCase();
        return (
          !query ||
          `${entry.leave_type_name} ${entry.reason} ${entry.note}`
            .toLowerCase()
            .includes(query)
        );
      }),
    [entries, filter, search],
  );

  const canRequest = useCallback(
    (entry: LeaveEntry) =>
      entry.user === user?.id && ["draft", "needs_info"].includes(entry.status),
    [user?.id],
  );
  // Zelf goedkeuren mag alleen buiten een organisatie; daarbinnen fiatteert een
  // manager of beheerder de aanvraag van iemand anders.
  const canApprove = useCallback(
    (entry: LeaveEntry) =>
      entry.status === "requested" &&
      (entry.user === user?.id
        ? !organizationId
        : capabilities.canApproveLeave),
    [capabilities.canApproveLeave, organizationId, user?.id],
  );
  const canDelete = useCallback(
    (entry: LeaveEntry) => entry.user === user?.id,
    [user?.id],
  );
  const isSelectable = useCallback(
    (entry: LeaveEntry) => canDelete(entry) || canRequest(entry) || canApprove(entry),
    [canApprove, canDelete, canRequest],
  );

  const selectableEntries = useMemo(
    () => visibleEntries.filter(isSelectable),
    [isSelectable, visibleEntries],
  );
  const selectedEntries = useMemo(
    () => selectableEntries.filter((entry) => selected.includes(entry.id)),
    [selectableEntries, selected],
  );
  const allSelected =
    selectableEntries.length > 0 && selectedEntries.length === selectableEntries.length;

  const bulkActions: Array<{
    action: BulkAction;
    label: string;
    entries: LeaveEntry[];
    danger?: boolean;
  }> = [
    {
      action: "request",
      label: "Aanvragen",
      entries: selectedEntries.filter(canRequest),
    },
    {
      action: "approve",
      label: "Goedkeuren",
      entries: selectedEntries.filter(canApprove),
    },
    {
      action: "delete",
      label: "Verwijderen",
      entries: selectedEntries.filter(canDelete),
      danger: true,
    },
  ];

  const toggleSelected = (entryId: number) =>
    setSelected((current) =>
      current.includes(entryId)
        ? current.filter((id) => id !== entryId)
        : [...current, entryId],
    );

  const runBulkAction = async (action: BulkAction, targets: LeaveEntry[]) => {
    setMenuOpen(false);
    if (targets.length === 0) return;
    if (
      action === "delete" &&
      !window.confirm(
        `${targets.length} verlofregel(s) definitief verwijderen? ` +
          "Goedgekeurde uren komen terug op je saldo.",
      )
    ) {
      return;
    }
    setBulkWorking(true);
    setError("");
    setSuccess("");
    try {
      const context = organizationId
        ? `organization=${organizationId}`
        : "context=personal";
      const response = await api.post<BulkResult>(`/leave/entries/bulk/?${context}`, {
        action,
        ids: targets.map((entry) => entry.id),
      });
      const { succeeded, failed } = response.data;
      const done = { request: "aangevraagd", approve: "goedgekeurd", delete: "verwijderd" }[
        action
      ];
      if (succeeded.length) setSuccess(`${succeeded.length} verlofregel(s) ${done}.`);
      if (failed.length) {
        setError(
          `${failed.length} regel(s) overgeslagen: ${[
            ...new Set(failed.map((row) => row.detail)),
          ].join(" ")}`,
        );
      }
      setSelected([]);
      await load();
    } catch (requestError: unknown) {
      setError(apiErrorMessage(requestError, "De actie kon niet worden uitgevoerd."));
    } finally {
      setBulkWorking(false);
    }
  };

  const removeEntry = async (entry: LeaveEntry) => {
    if (
      !window.confirm(
        "Dit verlof definitief verwijderen? Goedgekeurde uren komen terug op je saldo.",
      )
    ) {
      return;
    }
    setWorkingId(entry.id);
    setError("");
    try {
      await api.delete(`/leave/entries/${entry.id}/`);
      setSuccess("Het verlof is verwijderd.");
      await load();
    } catch (requestError: unknown) {
      setError(apiErrorMessage(requestError, "Het verlof kon niet worden verwijderd."));
    } finally {
      setWorkingId(null);
    }
  };

  const transition = async (entry: LeaveEntry, targetStatus: LeaveStatus) => {
    setWorkingId(entry.id);
    setError("");
    try {
      await api.post(`/leave/entries/${entry.id}/transition/`, {
        target_status: targetStatus,
      });
      setSuccess("De status is bijgewerkt.");
      await load();
    } catch {
      setError("De statuswijziging kon niet worden uitgevoerd.");
    } finally {
      setWorkingId(null);
    }
  };

  return (
    <div className="entries-page">
      <PageHeader
        eyebrow={organization?.name ?? "Persoonlijk"}
        title="Mijn verlof"
        description="Bekijk geplande, aangevraagde en goedgekeurde verlofuren."
        actions={
          <Link className="button primary" to="/app/requests/new">
            <CalendarPlus size={18} /> Nieuw verlof
          </Link>
        }
      />
      {error && <ErrorNotice>{error}</ErrorNotice>}
      {success && <SuccessNotice>{success}</SuccessNotice>}

      <div className="list-toolbar">
        <div className="filter-tabs" role="tablist" aria-label="Statusfilter">
          {filters.map((item) => (
            <button
              key={item.value}
              className={filter === item.value ? "active" : ""}
              onClick={() => setFilter(item.value)}
            >
              {item.label}
              {item.value !== "all" && (
                <span>
                  {entries.filter((entry) => entry.status === item.value).length}
                </span>
              )}
            </button>
          ))}
        </div>
        <label className="search-field">
          <Search size={17} />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Zoeken"
          />
        </label>
      </div>

      {!loading && selectableEntries.length > 0 && (
        <div className="bulk-toolbar">
          <button
            className="button subtle"
            type="button"
            disabled={bulkWorking}
            onClick={() =>
              setSelected(
                allSelected ? [] : selectableEntries.map((entry) => entry.id),
              )
            }
          >
            <ListChecks size={17} />{" "}
            {allSelected ? "Selectie wissen" : "Alle regels selecteren"}
          </button>
          <span className="bulk-count" aria-live="polite">
            {selectedEntries.length === 0
              ? "Niets geselecteerd"
              : `${selectedEntries.length} van ${selectableEntries.length} geselecteerd`}
          </span>
          <div className="bulk-menu" ref={menuRef}>
            <button
              className="button primary"
              type="button"
              disabled={selectedEntries.length === 0 || bulkWorking}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            >
              {bulkWorking ? "Bezig..." : "Acties"} <ChevronDown size={16} />
            </button>
            {menuOpen && (
              <div className="bulk-menu-panel" role="menu">
                {bulkActions.map((item) => (
                  <button
                    key={item.action}
                    type="button"
                    role="menuitem"
                    className={item.danger ? "danger" : ""}
                    disabled={item.entries.length === 0}
                    onClick={() => void runBulkAction(item.action, item.entries)}
                  >
                    {item.label}
                    <span>{item.entries.length}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <LoadingState label="Verlof laden..." />
      ) : (
        <section className="surface data-list-surface">
          {visibleEntries.length === 0 ? (
            <div className="empty-state">
              <CalendarPlus size={28} />
              <h2>Geen verlof gevonden</h2>
              <p>Pas je filter aan of voeg een nieuwe periode toe.</p>
              <Link className="button primary" to="/app/requests/new">
                Nieuw verlof
              </Link>
            </div>
          ) : (
            <div className="request-list">
              {visibleEntries.map((entry) => {
                const isOwn = entry.user === user?.id;
                const canApproveRow =
                  capabilities.canApproveLeave &&
                  !isOwn &&
                  entry.status === "requested";
                const selectable = isSelectable(entry);
                return (
                  <article
                    className={`request-row${
                      selected.includes(entry.id) ? " is-selected" : ""
                    }`}
                    key={entry.id}
                  >
                    <label
                      className={`request-select${selectable ? "" : " is-empty"}`}
                    >
                      {selectable && (
                        <input
                          type="checkbox"
                          checked={selected.includes(entry.id)}
                          disabled={bulkWorking}
                          aria-label={`${entry.leave_type_name} van ${formatDate(
                            entry.start_date,
                          )} selecteren`}
                          onChange={() => toggleSelected(entry.id)}
                        />
                      )}
                    </label>
                    <span className="request-date-block">
                      <strong>{formatDate(entry.start_date, "d")}</strong>
                      <small>{formatDate(entry.start_date, "MMM")}</small>
                    </span>
                    <div className="request-main">
                      <div>
                        <strong>{entry.leave_type_name}</strong>
                        <StatusBadge status={entry.status} />
                      </div>
                      <p>
                        {formatDate(entry.start_date)}
                        {entry.end_date !== entry.start_date
                          ? ` t/m ${formatDate(entry.end_date)}`
                          : ""}{" "}
                        · {formatMinutes(entry.requested_minutes)}
                      </p>
                      {entry.reason && <small>{entry.reason}</small>}
                    </div>
                    <div className="request-owner">
                      {organizationId && <span>{isOwn ? "Jij" : entry.user_name}</span>}
                    </div>
                    <div className="row-actions">
                      {entry.status === "draft" && isOwn && (
                        <button
                          className="button small primary"
                          disabled={workingId === entry.id}
                          onClick={() => void transition(entry, "requested")}
                        >
                          <Check size={16} /> Aanvragen
                        </button>
                      )}
                      {entry.status === "requested" && isOwn && !organizationId && (
                        <>
                          <button
                            className="icon-button success"
                            title="Goedkeuren"
                            aria-label="Goedkeuren"
                            disabled={workingId === entry.id}
                            onClick={() => void transition(entry, "approved")}
                          >
                            <Check size={17} />
                          </button>
                          <button
                            className="icon-button danger"
                            title="Afwijzen"
                            aria-label="Afwijzen"
                            disabled={workingId === entry.id}
                            onClick={() => void transition(entry, "rejected")}
                          >
                            <X size={17} />
                          </button>
                        </>
                      )}
                      {entry.status === "needs_info" && isOwn && (
                        <button
                          className="button small primary"
                          disabled={workingId === entry.id}
                          onClick={() => void transition(entry, "requested")}
                        >
                          <Check size={16} /> Opnieuw indienen
                        </button>
                      )}
                      {canApproveRow && (
                        <>
                          <button
                            className="icon-button success"
                            title="Goedkeuren"
                            aria-label="Goedkeuren"
                            onClick={() => void transition(entry, "approved")}
                          >
                            <Check size={17} />
                          </button>
                          <button
                            className="icon-button warning"
                            title="Vraag om toelichting"
                            aria-label="Vraag om toelichting"
                            onClick={() => void transition(entry, "needs_info")}
                          >
                            <MessageSquareMore size={17} />
                          </button>
                          <button
                            className="icon-button danger"
                            title="Afwijzen"
                            aria-label="Afwijzen"
                            onClick={() => void transition(entry, "rejected")}
                          >
                            <X size={17} />
                          </button>
                        </>
                      )}
                      {isOwn && !["cancelled", "rejected"].includes(entry.status) && (
                        <>
                          <Link
                            className="icon-button"
                            title="Verlof wijzigen"
                            aria-label={`${entry.leave_type_name} wijzigen`}
                            to={`/app/requests/${entry.id}/edit?return_to=/app/requests`}
                          >
                            <Pencil size={16} />
                          </Link>
                          <button
                            className="icon-button"
                            title="Annuleren"
                            aria-label="Annuleren"
                            onClick={() => void transition(entry, "cancelled")}
                          >
                            <X size={17} />
                          </button>
                        </>
                      )}
                      {isOwn && (
                        <button
                          className="icon-button danger"
                          title="Verlof verwijderen"
                          aria-label={`${entry.leave_type_name} verwijderen`}
                          disabled={workingId === entry.id}
                          onClick={() => void removeEntry(entry)}
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
